import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { ROUTES } from 'app/core/constants/routes';
import { AuthService } from 'app/modules/auth/services/auth.service';
import { OperatorAuthService } from 'app/modules/operator/services/operator-auth.service';
import { SKIP_AUTH_REFRESH } from 'app/core/interceptors/http-context-tokens';
import { LoggerService } from 'app/shared/services/logger.service';
import { redact } from 'app/shared/utils/sensitive-keys';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';

type ClientApp = 'agent' | 'operator';

/**
 * Auth is an httpOnly cookie set by the API (salis-be WebAuthCookie). Every
 * API call goes out withCredentials + `X-Client-App` - the header is what
 * lets the API use the cookie (CSRF protection) and picks which cookie: the
 * agent dashboard and the operator pages are separate logins.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing: Record<ClientApp, boolean> = { agent: false, operator: false };
  private refreshDone: Record<ClientApp, BehaviorSubject<boolean | null>> = {
    agent: new BehaviorSubject<boolean | null>(null),
    operator: new BehaviorSubject<boolean | null>(null),
  };

  constructor(
    private router: Router,
    private injector: Injector,
    private logger: LoggerService
  ) {}

  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  private get operatorAuthService(): OperatorAuthService {
    return this.injector.get(OperatorAuthService);
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const app = this.clientApp(req);
    req = this.withAuth(req, app);

    const startedAt = Date.now();
    this.logger.debug('API', `-> ${req.method} ${req.url}`, redact(req.body));

    return next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          this.logger.debug(
            'API',
            `<- ${event.status} ${req.method} ${req.url} (${Date.now() - startedAt}ms)`,
            redact(event.body)
          );
        }
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.warn(
          'API',
          `<- ${error.status} ${req.method} ${req.url} (${Date.now() - startedAt}ms)`,
          redact(error.error)
        );

        if (error.status >= 500) {
          Sentry.captureMessage(`API server error: ${req.method} ${req.url}`, {
            level: 'error',
            extra: { status: error.status, response: redact(error.error) },
          });
        }

        if (error.status === 401 && !req.context.get(SKIP_AUTH_REFRESH) && this.isLoggedIn(app)) {
          return this.handle401(req, next, app);
        }
        return throwError(() => error);
      })
    );
  }

  private clientApp(req: HttpRequest<any>): ClientApp {
    return req.url.startsWith(`${environment.apiUrl}/operator/`) ? 'operator' : 'agent';
  }

  private isLoggedIn(app: ClientApp): boolean {
    return app === 'operator' ? this.operatorAuthService.isLoggedIn() : this.authService.isLoggedIn();
  }

  private withAuth(req: HttpRequest<any>, app: ClientApp): HttpRequest<any> {
    if (!req.url.startsWith(environment.apiUrl)) {
      return req;
    }

    // Re-derived on every pass (including retries after a refresh), so a
    // legacy token that was just exchanged for a cookie isn't sent again.
    let headers = req.headers.delete('Authorization').set('X-Client-App', app);
    if (app === 'agent') {
      // Not remembered -> the API issues a browser-session cookie.
      headers = headers.set('X-Remember-Me', this.authService.isRemembered() ? '1' : '0');
      // One-time migration from pre-cookie builds (AuthService.migrateLegacyToken).
      const legacyToken = this.authService.getLegacyToken();
      if (legacyToken) {
        headers = headers.set('Authorization', `Bearer ${legacyToken}`);
      }
    }

    return req.clone({ withCredentials: true, headers });
  }

  private handle401(
    req: HttpRequest<any>,
    next: HttpHandler,
    app: ClientApp
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing[app]) {
      this.isRefreshing[app] = true;
      this.refreshDone[app].next(null);

      const refresh$ = app === 'operator' ? this.operatorAuthService.refreshToken() : this.authService.refreshToken();

      return refresh$.pipe(
        switchMap(() => {
          this.isRefreshing[app] = false;
          this.refreshDone[app].next(true);
          // The browser now holds the rotated cookie; just replay the request.
          return next.handle(this.withAuth(req, app));
        }),
        catchError((error) => {
          this.isRefreshing[app] = false;
          // Only a definitive rejection ends the session; a network error,
          // 429 or 5xx during refresh keeps the session for the next attempt.
          if (error?.status === 401 || error?.status === 403) {
            if (app === 'operator') {
              this.operatorAuthService.clearSession();
              this.router.navigate([ROUTES.OPERATOR.LOGIN]);
            } else {
              this.authService.clearSession();
              this.router.navigate(['/auth/login']);
            }
          }
          // Release requests queued behind this refresh instead of leaving them hanging.
          this.refreshDone[app].error(error);
          this.refreshDone[app] = new BehaviorSubject<boolean | null>(null);
          return throwError(() => error);
        })
      );
    }

    return this.refreshDone[app].pipe(
      filter((done) => done !== null),
      take(1),
      switchMap(() => next.handle(this.withAuth(req, app)))
    );
  }
}
